import { Product, SaleRow, Vat } from "@/models/base";
import { VatService } from "@/services/vat-service";
import { durationToProductQty } from "../duration";
import { EventEmitter } from "../event-emitter";
import { roundTo8Decimals, roundToDecimals, truncateToDecimals } from "../numbers";
import {CurrencyService} from "@/services/currency-service";
import Currency from "@/models/base/currency";
import { ProductService } from "@/services";

export interface IOpenProductSearchBrowseData {
	searchString?:string;
}

export class ProductEditRowState extends EventEmitter {
	protected _productDescription:string = "";
	get productDescription():string{
		return this._productDescription;
	}
	set productDescription(value:string){
		this._productDescription = value;
	}
	amount:number = 0.0
	unitPrice:number = 0.0
	discount:number = 0.0
	unitBuyPrice:number = 0.0
	product:null | Product = null
	netto:number = 0.0
	bruto:number = 0.0
	leeggoedTax:number = 0.0
	ecoboniTax:number = 0.0;
	accijnzenTax:number = 0.0;
	customVatExclusiveTax:number = 0.0;
	customVatInclusiveTax:number = 0.0;
	duration:number = 0
	unit:string = ""
	vatItem:Vat = VatService.getVats()[0]
	toggleTimePicker:boolean = false;
	contactDiscount:number = 0;
	currency:number = 1;
	currencies:Currency[] =  CurrencyService.getCurrencies();

	currencyRate:number = this.getCurrencyRate;
	toCreateProduct:Product | null = null;

	//multivaluta
	unitPriceMc:number = 0.0
	nettoMc:number = 0.0
	brutoMc:number = 0.0

	//backorder
	delivered:number = 0

	get vat(): number {
		return this.vatItem.Value;
	}

	get vatMultiplier(): number {
		return 1 + (this.vat / 100.0);
	}

	get getCurrencyRate(): number {
		if (this.currencies.length == 0){
			return 1;
		}
		let foundCurrency = this.currencies.filter((c:Currency) => c.ID == this.currency)[0];
		if (!foundCurrency) return 1;
		return foundCurrency.Rate || 1;
	}

	get totalLeeggeod():number {
		return this.leeggoedTax * this.amount;
	}

	setNetto(value: number, preventUnitReset?: boolean) {
		if (preventUnitReset) {
			let discountPerUnit = this.unitPrice / 100 * this.discount;
			let discountPerUnitMc = this.unitPriceMc / 100 * this.discount;
			//prevent reset of unitPrice (autocomplete/search)
			this.netto = (this.unitPrice - discountPerUnit + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount;
			this.nettoMc = (this.unitPriceMc - discountPerUnitMc + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount;
			this.updateBruto();
		} else {
			this.netto = value;
			this.nettoMc = value / this.currencyRate;
			this.updateBruto();
			if (this.amount !== 0 && value !== 0) {
				let valueForUnitPrice = value - this.getTotalVatInclusiveTaxes();
				this.unitPrice = roundTo8Decimals(valueForUnitPrice / (this.amount * (1 - this.discount / 100)));
				this.unitPriceMc = this.unitPrice / this.currencyRate;
			}
		}
	}

	getVat():number{
		let roundedNetto = roundToDecimals(this.netto, 2);
		return roundedNetto * (this.vatMultiplier - 1);
	}

	getMcVat():number{
		let roundedNettoMc = roundToDecimals(this.nettoMc, 2);
		return roundedNettoMc * (this.vatMultiplier - 1);
	}

	getVatFromBruto(bruto:number):number {
		return (bruto - this.getTotalVatExclusiveTaxes()) - ((bruto - this.getTotalVatExclusiveTaxes()) / this.vatMultiplier);
	}

	getTotalVatExclusiveTaxes():number{
		return this.totalLeeggeod + (this.customVatExclusiveTax * this.amount);
	}

	setBruto(value: number, preventUnitReset?: boolean) {
		let netto = value - this.getVatFromBruto(value) - this.getTotalVatExclusiveTaxes();
		this.setNetto(truncateToDecimals(netto, 4), preventUnitReset);
		this.bruto = value;
		this.brutoMc = value / this.currencyRate;
	}

	setBrutoPerUnit(value: number) {
		// Calculate netto per unit from bruto per unit (remove VAT)
		// Truncate to 4 decimals to prevent double-rounding issues (e.g. 280 bruto → 280.01)
		let nettoPerUnit = truncateToDecimals(value / this.vatMultiplier, 4);
		// Subtract ecoboni and accijnzen taxes (these are added separately in setUnitPrice)
		let nettoPerUnitWithoutTaxes = nettoPerUnit - this.ecoboniTax - this.accijnzenTax;
		// Account for discount: unitPrice = nettoPerUnit / (1 - discount/100)
		let unitPrice = this.discount !== 0
			? roundTo8Decimals(nettoPerUnitWithoutTaxes / (1 - this.discount / 100))
			: nettoPerUnitWithoutTaxes;
		// Set the unit price, which will recalculate totals (netto * amount, then bruto)
		this.setUnitPrice(unitPrice);
	}

	setBrutoPerUnitMc(value: number) {
		// Calculate netto per unit from bruto per unit (remove VAT)
		// Truncate to 4 decimals to prevent double-rounding issues (e.g. 280 bruto → 280.01)
		let nettoPerUnit = truncateToDecimals(value / this.vatMultiplier, 4);
		// Subtract ecoboni and accijnzen taxes (these are added separately in setUnitMcPrice)
		let nettoPerUnitWithoutTaxes = nettoPerUnit - this.ecoboniTax - this.accijnzenTax;
		// Account for discount: unitPrice = nettoPerUnit / (1 - discount/100)
		let unitPriceMc = this.discount !== 0
			? roundTo8Decimals(nettoPerUnitWithoutTaxes / (1 - this.discount / 100))
			: nettoPerUnitWithoutTaxes;
		// Set the unit price, which will recalculate totals (netto * amount, then bruto)
		this.setUnitMcPrice(unitPriceMc);
	}

	getTotalVatInclusiveTaxesPerUnit():number{
		return this.ecoboniTax + this.accijnzenTax + this.customVatInclusiveTax;
	}

	getTotalVatInclusiveTaxes():number{
		return this.getTotalVatInclusiveTaxesPerUnit() * this.amount;
	}

	setNettoMc(value: number, preventUnitReset?: boolean) {
		if (preventUnitReset) {
			let discountPerUnit = this.unitPrice / 100 * this.discount;
			let discountPerUnitMc = this.unitPriceMc / 100 * this.discount;
			this.netto = (this.unitPrice - discountPerUnit + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount;
			this.nettoMc = (this.unitPriceMc - discountPerUnitMc + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount;
			this.updateBruto();
		} else {
			this.nettoMc = value;
			this.netto = value * this.currencyRate;
			this.updateBruto();
			if (this.amount !== 0 && value !== 0) {
				let valueForUnitPriceMc = value - this.getTotalVatInclusiveTaxes() / this.currencyRate;
				this.unitPriceMc = roundTo8Decimals(valueForUnitPriceMc / (this.amount * (1 - this.discount / 100)));
				this.unitPrice = this.unitPriceMc * this.currencyRate;
			}
		}
	}

	setBrutoMc(value: number, preventUnitReset?: boolean) {
		let brutoInDefault = value * this.currencyRate;
		let nettoMc = value - this.getVatFromBruto(brutoInDefault) / this.currencyRate - this.getTotalVatExclusiveTaxes() / this.currencyRate;
		this.setNettoMc(truncateToDecimals(nettoMc, 4), preventUnitReset);
		this.brutoMc = value;
		this.bruto = value * this.currencyRate;
	}

	updateBruto(){
		this.bruto = roundTo8Decimals(this.netto + this.getVat() + this.getTotalVatExclusiveTaxes());
		this.brutoMc = roundTo8Decimals(this.nettoMc + this.getMcVat()+ this.getTotalVatExclusiveTaxes());
	}

	setUnitPrice(value: number) {
		this.unitPrice = value;
		this.unitPriceMc = value / this.currencyRate;
		let discountPerUnit = this.unitPrice / 100 * this.discount;
		let discountPerUnitMc = this.unitPriceMc / 100 * this.discount;
		this.netto = roundTo8Decimals((this.unitPrice - discountPerUnit + this.getTotalVatInclusiveTaxesPerUnit())* this.amount);
		this.nettoMc = roundTo8Decimals((this.unitPriceMc - discountPerUnitMc + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount);
		this.updateBruto();
	}

	setUnitMcPrice(value: number) {
		this.unitPrice = value * this.currencyRate;
		this.unitPriceMc = value;
		let discountPerUnit = this.unitPrice / 100 * this.discount;
		let discountPerUnitMc = this.unitPriceMc / 100 * this.discount;
		this.netto = roundTo8Decimals((this.unitPrice - discountPerUnit + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount);
		this.nettoMc = roundTo8Decimals((this.unitPriceMc - discountPerUnitMc + this.getTotalVatInclusiveTaxesPerUnit()) * this.amount);
		this.updateBruto();
	}

	setLeeggoedPrice(value: number){
		this.leeggoedTax = value;
		this.updateBruto();
	}

	setEcoboniTax(value:number){
		this.ecoboniTax = value;
		this.setNetto(0, true);
	}
	setAccijnzenTax(value:number){
		this.accijnzenTax = value;
		this.setNetto(0, true);
	}

	setCustomVatExclusiveTax(value:number){
		this.customVatExclusiveTax = value;
		this.setNetto(0, true);
	}

	setCustomVatInclusiveTax(value:number){
		this.customVatInclusiveTax = value;
		this.setNetto(0, true);
	}

	setDiscount(value: number) {
		this.discount = value;
		this.setUnitPrice(this.unitPrice);
	}

	setDuration(value: number) {
		this.duration = value;
		this.amount = durationToProductQty(this.duration);
	}

	openProductSearchBrowse(input?:string){
		let data:IOpenProductSearchBrowseData = {searchString: input};
		this.emit("open-product-search-browse", data as IOpenProductSearchBrowseData);
	}

	openProductCreateView(input?:string){
		this.emit("open-create-product-view", input);
	}

	cancelCreateProduct(){
		this.toCreateProduct = null;
	}

	async finishCreateProduct(product: Product) {
		let createdProduct = await ProductService.getProduct(product.ID);
		this.toCreateProduct = null;
		this.emit("finish-create-product", createdProduct);
	}


	public setRowValues(row:SaleRow){
		row.UnitName = this.unit;
		if (this.product){
			row.ProductSku = this.product.Sku || "";
		}else{
			row.ProductSku = "";
		}
		row.ProductDescription = this.productDescription;
		row.Amount = this.amount;
		row.UnitPrice = this.unitPrice;
		row.Discount = this.discount;
		row.Vat = this.vat;
		row.Duration = this.duration;
		row.Product = this.product;
		if(this.duration){
			row.Amount = durationToProductQty(this.duration);
		}
		row.UnitBuyPrice = this.unitBuyPrice;
		row.EcoboniTax = this.ecoboniTax;
		row.AccijnzenTax = this.accijnzenTax;
		row.CustomVatExclusiveTax = this.customVatExclusiveTax;
		row.CustomVatInclusiveTax = this.customVatInclusiveTax;
		row.LeeggoedTax = this.leeggoedTax;
		row.Delivered = this.delivered;
	}
}
